import React, { useEffect, useState } from "react";
import {
  Button,
  FormControlLabel,
  FormLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  updateData,
} from "../../actions/actionItems";
import DraggableItemList from "../../subcomponents/DNDComponent";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { SpaPropertyEnum } from "../../../constants/NavbarList";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import { spaTooltipHeaders } from "../../../constants/TooltipInformation";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationMessages,
  validationType,
} from "../../actions/validations";
import { parseSpaBooleanField } from "../../../constants/SpaBooleanFields";

const SpaLogTypes = ({
  data,
  fieldValues,
}: {
  data: (value: any) => void;
  fieldValues: any;
}) => {
  const [logTypeName, setLogTypeName] = useState<string | null>(null);
  const [active, setActive] = useState<boolean>(true);
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [items, setItems] = useState<any[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const [logTypeNameError, setLogTypeNameError] = useState<boolean>(false);
  const [logTypeNameErrorMessage, setLogTypeNameErrorMessage] =
    useState<string>("");

  useEffect(() => {
    fieldValues && setItems(fieldValues);
  }, []);

  useEffect(() => {
    data(items);
    setSelectedId(checkData(items, selectedId) ? selectedId : null);
  }, [items]);

  const validateLogTypeFields = (): boolean => {
    if (!logTypeName || logTypeName.length === 0) {
      setAlertMessage("Log Type Name is mandatory");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }
    setLogTypeNameError(false);
    setLogTypeNameErrorMessage("");
    if (
      !validateString(
        logTypeName,
        validationLength.logtypeDescription,
        validationType.NAME,
      )
    ) {
      setLogTypeNameError(true);
      setLogTypeNameErrorMessage(validationMessages.logtypeDescription);
      return false;
    }

    const listForDuplicate = selectedId
      ? items.filter((i: any) => String(i.id) !== String(selectedId))
      : items;
    if (
      checkDuplicate(listForDuplicate, DNDFieldsEnum.LOGTYPENAME, logTypeName)
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return false;
    }

    return true;
  };

  const updateItem = () => {
    if (!validateLogTypeFields()) {
      return false;
    }
    setItems((prev) =>
      prev.map((item: any) =>
        item.id === selectedId ? { ...item, ...payload } : item,
      ),
    );
    editItem(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${SpaPropertyEnum.logTypes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const addItem = () => {
    if (!validateLogTypeFields()) {
      return false;
    }
    setItems((prev) =>
      updateData(
        prev?.length
          ? [...prev, { id: prev.length + 1, ...payload }]
          : [{ id: 1, ...payload }],
      ),
    );
    setLastUpdatedRecord(payload);
    editItem(null);
    setEditClicked(false);
  };

  const editItem = (list: any) => {
    list?.id && setSelectedId(list.id);
    if (list != null) {
      setEditClicked(true);
      setLogTypeName(list["Log Type Name"] ?? null);
      setActive(parseSpaBooleanField(list?.Active ?? list?.IsActive));
    } else {
      setLogTypeName(null);
      setActive(true);
      setLogTypeNameError(false);
      setLogTypeNameErrorMessage("");
    }
  };

  const deleteItemById = (id: number) => {
    setItems(deleteItem(items, id));
  };

  const payload = {
    "Log Type Name": logTypeName,
    Active: active,
  };

  return (
    <Stack direction="column" gap="10px">
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          padding: "20px",
        }}
      >
        <Stack
          direction="row"
          width="100%"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={textStyle}>
            Log Types
            <Tooltip
              title={tooltipComponent(
                spaTooltipHeaders?.logTypeHeader ?? "Log Types",
              )}
            >
              <InfoIcon fontSize="small" />
            </Tooltip>
          </Typography>
          <Stack direction="row" gap="10px">
            {editClicked && (
              <ClearButton
                click={() => {
                  editItem(null);
                  setSelectedId(null);
                }}
              />
            )}
            {lastUpdatedRecord && (
              <CopyButton click={() => editItem(lastUpdatedRecord)} />
            )}
          </Stack>
        </Stack>
        <Stack direction="column" width="100%" gap="20px" flexWrap="wrap">
          <Stack direction="column">
            <Stack direction="row">
              <FormLabel>Log Type Name</FormLabel>
              <Tooltip
                title={tooltipComponent(spaTooltipHeaders.logTypeLogType)}
              >
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Stack>
            <TextField
              variant="outlined"
              placeholder="Log Type Name"
              value={logTypeName ?? ""}
              onChange={(e) => setLogTypeName(e.target.value || null)}
              error={logTypeNameError}
              helperText={logTypeNameError ? logTypeNameErrorMessage : ""}
              fullWidth
            />
          </Stack>
          <Stack direction="row" alignItems="center">
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label="Active"
            />
          </Stack>
        </Stack>
        <Stack direction="row" justifyContent="flex-end">
          <Button
            variant="contained"
            sx={{
              cursor: "pointer",
              border: "1px solid gray",
              borderRadius: "10px",
              background: "darkgreen",
              color: "white",
            }}
            onClick={() => (selectedId ? updateItem() : addItem())}
          >
            {selectedId ? buttonValues.UPDATE : buttonValues.ADD} Log Type
          </Button>
        </Stack>
        <Stack direction="column" gap="10px" mt={4}>
          <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
            Do not use sample data. Those are for demo purposes only!
          </Typography>
          <Typography variant="h6">Log Types Available</Typography>
          {items ? (
            <DraggableItemList
              itemData={items}
              onOrderUpdate={(order) => setItems(order)}
              onEdit={editItem}
              onDelete={deleteItemById}
              code={DNDFieldsEnum.LOGTYPENAME}
              name={DNDFieldsEnum.ACTIVE}
            />
          ) : (
            <Typography variant="body2">No Log Types Available</Typography>
          )}
        </Stack>
      </Paper>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default SpaLogTypes;
